import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

const cyan = '#00BFFF';

/**
 * BookCover renders a book cover image when a properly licensed/local asset is
 * available. When no cover is provided (common for third-party titles where we
 * do not download copyrighted images), it renders a calm, editorial text-based
 * placeholder built from the title and author.
 *
 * Images are lazy-loaded and use a fixed aspect ratio to prevent layout shift.
 */
const BookCover = ({ src, alt, title, author, height = 240, featured = false, eager = false, fit = 'cover' }) => {
    const [failed, setFailed] = useState(false);
    const showPlaceholder = !src || failed;

    const accentBorder = featured ? alpha(cyan, 0.45) : alpha(cyan, 0.18);

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '2 / 3',
                maxHeight: height,
                minHeight: height * 0.75,
                borderRadius: 2,
                overflow: 'hidden',
                border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? accentBorder : alpha('#000', 0.1)}`,
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#0B1424' : '#EEF2F7'),
                boxShadow: featured
                    ? `0 12px 40px ${alpha('#000', 0.45)}, 0 0 0 1px ${alpha(cyan, 0.15)}`
                    : `0 4px 18px ${alpha('#000', 0.25)}`,
                display: 'flex',
                flexShrink: 0,
            }}
        >
            {showPlaceholder ? (
                <Box
                    aria-label={alt || `${title} by ${author}`}
                    role="img"
                    sx={{
                        width: '100%',
                        height: '100%',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        background: (theme) =>
                            theme.palette.mode === 'dark'
                                ? `linear-gradient(155deg, #10203A 0%, #0A1628 55%, #0B1B33 100%)`
                                : `linear-gradient(155deg, #F4F7FB 0%, #E7EDF5 100%)`,
                    }}
                >
                    <Box
                        sx={{
                            width: 28,
                            height: 3,
                            borderRadius: 2,
                            backgroundColor: alpha(cyan, 0.7),
                            boxShadow: `0 0 10px ${alpha(cyan, 0.6)}`,
                        }}
                    />
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                lineHeight: 1.25,
                                color: 'text.primary',
                                letterSpacing: '-0.01em',
                            }}
                        >
                            {title}
                        </Typography>
                        {author && (
                            <Typography
                                sx={{
                                    mt: 0.75,
                                    fontSize: '0.7rem',
                                    color: 'text.secondary',
                                    lineHeight: 1.35,
                                }}
                            >
                                {author}
                            </Typography>
                        )}
                    </Box>
                    <Typography
                        sx={{
                            fontSize: '0.55rem',
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: alpha(cyan, 0.7),
                        }}
                    >
                        ReaderFLEX Library
                    </Typography>
                </Box>
            ) : (
                <Box
                    component="img"
                    src={src}
                    alt={alt || `${title} by ${author}`}
                    loading={eager ? 'eager' : 'lazy'}
                    decoding="async"
                    onError={() => setFailed(true)}
                    sx={{ width: '100%', height: '100%', objectFit: fit, display: 'block' }}
                />
            )}
        </Box>
    );
};

export default BookCover;
